import { useEffect } from "react";
import { Check, LockKeyhole } from "lucide-react";

import * as Api from "@modules/api";
import { createPageElement } from "@modules/content/creators/createPageElement";
import { usePageData } from "@modules/content/hooks/usePageData";
import { ContentPage } from "@modules/content/types";
import PageWithDynamicZoneLayout from "@components/layouts/content/PageWithDynamicZoneLayout";
import SkillDetailsCard from "@components/layouts/b2b/skill/SkillDetails";
import { getQuizActions, useAppSelector, useGetQuizActions } from "@lib/stores";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import { useGetContentProgress } from "@modules/content/hooks/useGetContentProgress";
import { ShadcnButton } from "@components/items/display/ShadcnButton";

export interface SkillDetailsParameters {
  image: string | null;
  skillIcon: string | null;
}

export const SkillDetails = createPageElement(
  ({ pageId, context, onPageLoading }, ref) => {
    const navigate = useAppNavigate();

    const { data: quizActionsData } = useGetQuizActions();

    const quizAction = quizActionsData?.[0];

    const { pageData, getParameterValues, refresh, isLoading, content } =
      usePageData<SkillDetailsParameters>(
        {
          pageId: pageId ?? "",
          context,
        },
        ref
      );

    const { image, skillIcon } = getParameterValues(["image", "skillIcon"]);

    const progress = useAppSelector(({ content: contentState }) =>
      contentState.progresses.find((p) => p.contentEntityId === content?.id)
    );

    useGetContentProgress(Boolean(content?.id), [content?.id ?? ""]);

    useEffect(() => {
      if (content?.id) {
        getQuizActions({ contentEntityIds: [content.id] });
      }
    }, [content?.id]);

    useEffect(() => {
      if (!pageData?.id || pageData.id !== pageId) {
        onPageLoading && onPageLoading(undefined, isLoading);
      } else {
        onPageLoading && onPageLoading(pageData, isLoading);
      }
    }, [pageData?.id, pageId, isLoading]);

    if (!pageData) return null;

    return (
      <div className="flex h-full flex-col items-start justify-start pt-10">
        <PageWithDynamicZoneLayout
          items={pageData?.dynamicZone ?? []}
          onRefresh={refresh}
          context={content || undefined}
          withContext
        >
          <div className="px-10">
            <SkillDetailsCard
              title={content?.title}
              totalSummaries={content?.chaptersCount}
              totalDuration={content?.durationMS}
              completedSummaries={progress?.completedItems || 0}
              description=""
              progress={Number(
                ((progress?.currentProgress || 0) * 100)?.toFixed(0)
              )}
              icon={skillIcon || undefined}
            />
          </div>
          {quizAction &&
          quizAction.action?.contentEntityQuizAction !==
            Api.Server.ContentEntityQuizAction.Disabled ? (
            <div className="px-10">
              <div className="mt-6 flex w-[705px] items-center justify-between rounded-[8px] border border-[#D9D9D9] bg-white p-[23px] dark:bg-black">
                <div className="flex items-center justify-start gap-[27px] text-[20px] font-medium text-b2b-primary">
                  <LockKeyhole size={27} color="#D7D7D8" />
                  {quizAction.action?.instructionText}
                </div>
                {quizAction.isSuccessful ? (
                  <div className="flex h-[33px] w-[33px] items-center justify-center rounded-full bg-[#D7D7D8] text-white dark:text-black">
                    <Check size={22} strokeWidth={3} />
                  </div>
                ) : (
                  <div>
                    <ShadcnButton
                      style={{
                        backgroundColor: quizAction.action?.tintColor,
                        color: "white",
                      }}
                      onClick={() => {
                        navigate(`/skill/${content?.id}/quiz`);
                      }}
                    >
                      {quizAction.action?.title}
                    </ShadcnButton>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </PageWithDynamicZoneLayout>
      </div>
    );
  }
);

export const SkillDetailsPage: ContentPage = {
  identifier: "PG:SKILL:DETAILS",
  page: SkillDetails,
};
